// Ordering rules for estimate attachments. Each attachment has a placement;
// list order is the order within a placement.

export const ATTACHMENT_GROUPS = [
  { placement: 'beforePricing', title: 'Before pricing' },
  { placement: 'afterPricing', title: 'After pricing' },
  { placement: 'end', title: 'At the end' },
  { placement: 'emailOnly', title: 'Email only' },
];

// Placements inside the signing document, in document order.
export const DOCUMENT_ZONES = ['beforePricing', 'afterPricing', 'end'];

export const toGroups = list =>
  Object.fromEntries(
    ATTACHMENT_GROUPS.map(({ placement }) => [
      placement,
      list.filter(a => a.placement === placement),
    ])
  );
export const flatten = groups =>
  ATTACHMENT_GROUPS.flatMap(({ placement }) => groups[placement]);

// The list with `key` moved to position `index` of `placement`.
export const moveTo = (list, key, placement, index) => {
  const item = list.find(a => a.key === key);
  const groups = toGroups(list.filter(a => a.key !== key));
  groups[placement].splice(index, 0, { ...item, placement });
  return flatten(groups);
};

// One step up or down the document, crossing the fixed pages (moving above
// "Pricing" makes a PDF come before pricing, and so on).
export const stepMove = (list, item, direction) => {
  const groups = toGroups(list);
  const zone = groups[item.placement];
  const index = zone.indexOf(item);
  const next = index + direction;
  if (next >= 0 && next < zone.length) {
    return moveTo(list, item.key, item.placement, next);
  }
  const zoneIndex = DOCUMENT_ZONES.indexOf(item.placement);
  const neighbour = DOCUMENT_ZONES[zoneIndex + direction];
  if (zoneIndex < 0 || !neighbour) return null;
  return moveTo(
    list,
    item.key,
    neighbour,
    direction < 0 ? groups[neighbour].length : 0
  );
};

// The roof photo pages move through the document like a PDF. In the
// combined list they're this pseudo-item; it can't go to "email only".
export const PHOTOS_KEY = '__roof_photos__';

export const withPhotos = (attachments, photoPosition) => {
  const groups = toGroups(attachments);
  const zone = groups[photoPosition.placement];
  zone.splice(Math.min(photoPosition.index, zone.length), 0, {
    key: PHOTOS_KEY,
    placement: photoPosition.placement,
    isPhotos: true,
  });
  return flatten(groups);
};

export const splitPhotos = list => {
  const photos = list.find(a => a.key === PHOTOS_KEY);
  const attachments = list.filter(a => a.key !== PHOTOS_KEY);
  if (!photos) return { attachments, photoPosition: null };
  const index = list
    .filter(a => a.placement === photos.placement)
    .indexOf(photos);
  return {
    attachments,
    photoPosition: { placement: photos.placement, index },
  };
};
